import React, { useState, useCallback, useRef } from "react";
import { findLineItemByBarcode } from "../../helper_functions/utils";
import type { IPurchaseOrderLineItem, TransferOrderLineItem } from "../../api_utils/types";
import { Button } from "@shopify/polaris";

type TransferOrderLineItems = Record<number, TransferOrderLineItem>;
type PurchaseOrderLineItems = Record<number, IPurchaseOrderLineItem>;

const barcodeEnterKeys = [
  "Enter",
  "NumpadEnter",
  "ArrowDown",
  "Tab",
];

export default function Scanner({
  lineItems,
  incrementLineItem,
}: {
  readonly lineItems: TransferOrderLineItems | PurchaseOrderLineItems;
  readonly incrementLineItem: (id: number) => void;
}): React.ReactElement {
  const [isScanningActive, setIsScanningActive] = useState(false);

  const processBarcode = useCallback(
    (currentBarcode: string) => {
      const lineItem = findLineItemByBarcode<TransferOrderLineItem | IPurchaseOrderLineItem>(lineItems, currentBarcode);

      if (lineItem) {
        incrementLineItem(lineItem.id);
      } else {
        shopify.toast.show(`Barcode not found: ${currentBarcode}`, {
          isError: true,
        });
      }
    },
    [lineItems, incrementLineItem],
  );

  const barcode = useRef<string>("");
  const timeout = useRef<ReturnType<typeof setTimeout>>(null);
  const barcodeScanEventListener = useCallback(
    (evt: KeyboardEvent) => {
      if (timeout.current) {
        clearInterval(timeout.current);
        timeout.current = null;
      }
      if (barcodeEnterKeys.includes(evt.key) || barcodeEnterKeys.includes(evt.code)) {
        if (barcode.current) {
          processBarcode(barcode.current);
        }
        barcode.current = "";
        evt.preventDefault();
      } else if (evt.key !== "Shift") {
        barcode.current += evt.key;
        timeout.current = setTimeout(() => {
          processBarcode(barcode.current);
          barcode.current = "";
        }, 200);
        evt.preventDefault();
      }
    },
    [processBarcode],
  );

  const barcodeScanEventListenerAdded = useRef<(evt: KeyboardEvent) => void>(null);

  const handleBarcodeScannerSelect = useCallback(() => {
    setIsScanningActive(true);
    if (barcodeScanEventListenerAdded.current) {
      document.removeEventListener("keydown", barcodeScanEventListenerAdded.current);
    }
    document.addEventListener("keydown", barcodeScanEventListener);
    barcodeScanEventListenerAdded.current = barcodeScanEventListener;
  }, [setIsScanningActive, barcodeScanEventListener]);

  const handleBarcodeScanStop = useCallback(() => {
    setIsScanningActive(false);
    document.removeEventListener("keydown", barcodeScanEventListenerAdded.current);
    barcodeScanEventListenerAdded.current = null;
    if (timeout.current) {
      clearInterval(timeout.current);
      timeout.current = null;
    }
    barcode.current = "";
  }, [setIsScanningActive]);

  return (
    <>
      {isScanningActive ? (
        <Button onClick={handleBarcodeScanStop} tone="critical">
          Stop scanning
        </Button>
      ) : (
        <Button onClick={handleBarcodeScannerSelect}>Scan barcodes</Button>
      )}
    </>
  );
}
